import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import StatusBadge from "components/ReportUI/Statusbadge";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Date and Dropdown components
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Autocomplete from "@mui/material/Autocomplete";

import { AccountsContext } from "context/AccountsContext";
import HaltReportTable from "./boatOrgTripReportTable";
import DateRangePicker from "layouts/DateTimerange/dateTimeRange"; // ✅ Custom Range Picker Imported
import DhundhooLoader from "../Loader/Loader";

function BoatOrgReport() {
  const [boatId, setBoatId] = useState("");
  const [tripData, setTripData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);

  const { fetchThings, things } = useContext(AccountsContext);

  useEffect(() => {
    fetchThings();
  }, []);

  // ✅ Aaj (Today) ki date nikalne ka logic
  const getTodayDateString = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const todayStr = getTodayDateString();
  const defaultStart = `${todayStr}T00:00`;
  const defaultEnd = `${todayStr}T23:59`;

  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // ✅ ALL PARAMETERS BINDING IN COLUMNS
  const columns = [
    { Header: "Boat Name", accessor: "boatName", width: "10%" },
    { Header: "Start Time", accessor: "tripStartTime", width: "15%" },
    { Header: "End Time", accessor: "tripEndTime", width: "15%" },
    {
      Header: "Status",
      accessor: "status",
      width: "10%",
      Cell: ({ value }) => (
        <StatusBadge
          value={value}
          trueLabel="Completed"
          falseLabel={value || "Pending"}
          trueValues={["completed", "complete", "done", "true", "1"]}
        />
      ),
    },
    { Header: "Passengers", accessor: "passengerCount", width: "5%" },
    { Header: "Fuel Start", accessor: "startFuel", width: "8%" },
    { Header: "Fuel End", accessor: "endFuel", width: "8%" },
    { Header: "Fuel Consumed", accessor: "fuelConsumed", width: "10%" },
    { Header: "Eng. Hrs Start", accessor: "startEngineHours", width: "10%" },
    { Header: "Eng. Hrs End", accessor: "endEngineHours", width: "10%" },
    { Header: "Total Eng. Hrs", accessor: "engineHours", width: "10%" },
    { Header: "Odo Start", accessor: "startOdometer", width: "10%" },
    { Header: "Odo End", accessor: "endOdometer", width: "10%" },
    { Header: "Start Location", accessor: "startLocation", width: "15%" },
    { Header: "End Location", accessor: "endLocation", width: "15%" },
    { Header: "Start Geofence", accessor: "startGeofence", width: "10%" },
    { Header: "End Geofence", accessor: "endGeofence", width: "10%" },
    { Header: "Dest. Geofence", accessor: "destinationGeofence", width: "10%" },
    { Header: "Comments", accessor: "comments", width: "15%" },
    { Header: "Created At", accessor: "createdAt", width: "12%" },
  ];

  const fetchTripReport = async () => {
    // if (!boatId) {
    //   setErrorMessage("Please select boat");
    //   openErrorSB();
    //   return;
    // }

    if (!startTime || !endTime) {
      setErrorMessage("Please select a date range first.");
      openErrorSB();
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setTripData([]);
    setSummary(null);

    try {
      const token = localStorage.getItem("token");

      // ✅ Ensure correct format (YYYY-MM-DDTHH:mm:ss)
      const formatDate = (date) => {
        if (!date) return "";
        return date.length === 16 ? `${date}:00` : date;
      };

      const startDate = formatDate(startTime);
      const endDate = formatDate(endTime);

      const url = `https://v3.dhundhoo.com/boat/trip/admin/orgTripReport`;

      const response = await axios.get(url, {
        params: {
          // boatId,
          startDate,
          endDate,
        },
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
      });

      const data = response?.data || {};

      // ✅ Summary safe handling with KM to NM conversion
      setSummary({
        totalTrips: data.totalTrips ?? 0,
        totalFuelConsumed:
          typeof data.totalFuelConsumed === "number"
            ? data.totalFuelConsumed.toFixed(2)
            : "0.00",
        totalDistance:
          typeof data.totalDistance === "number"
            ? (data.totalDistance * 0.539957).toFixed(2) // converted to NM
            : "0.00",
        totalEngineHours:
          typeof data.totalEngineHours === "number"
            ? data.totalEngineHours.toFixed(2)
            : "0.00",
      });

      if (!data.trips || data.trips.length === 0) {
        setErrorMessage("No trips found for selected date range");
        openErrorSB();
        return;
      }

      // ✅ Trip rows safe mapping
      const rows = data.trips.map((trip) => ({
        boatName: trip.boatName || "-",

        tripStartTime: trip.tripStartTime
          ? new Date(trip.tripStartTime).toLocaleString()
          : "-",

        tripEndTime: trip.tripEndTime
          ? new Date(trip.tripEndTime).toLocaleString()
          : "-",

        status: trip.status || "-",
        passengerCount: trip.passengerCount ?? 0,

        startFuel:
          typeof trip.startFuel === "number"
            ? trip.startFuel.toFixed(2)
            : "-",

        endFuel:
          typeof trip.endFuel === "number"
            ? trip.endFuel.toFixed(2)
            : "-",

        fuelConsumed:
          typeof trip.fuelConsumed === "number"
            ? trip.fuelConsumed.toFixed(2)
            : "0.00",

        startEngineHours:
          typeof trip.startEngineHours === "number"
            ? trip.startEngineHours.toFixed(2)
            : "-",

        endEngineHours:
          typeof trip.endEngineHours === "number"
            ? trip.endEngineHours.toFixed(2)
            : "-",

        engineHours:
          typeof trip.engineHours === "number"
            ? trip.engineHours.toFixed(2)
            : "0.00",

        startOdometer: trip.startOdometer ?? "-",
        endOdometer: trip.endOdometer ?? "-",

        // ✅ FIX: allow 0 values
        startLocation:
          trip.startLocation?.lat != null &&
            trip.startLocation?.lng != null
            ? `${trip.startLocation.lat}, ${trip.startLocation.lng}`
            : "-",

        endLocation:
          trip.endLocation?.lat != null &&
            trip.endLocation?.lng != null
            ? `${trip.endLocation.lat}, ${trip.endLocation.lng}`
            : "-",

        startGeofence: trip.startGeofence || "-",
        endGeofence: trip.endGeofence || "-",
        destinationGeofence: trip.destinationGeofence || "-",
        comments: trip.comments || "-",

        createdAt: trip.createdAt
          ? new Date(trip.createdAt).toLocaleString()
          : "-",
      }));

      setTripData(rows);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Failed to load trip report");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setBoatId("");
    setStartTime(defaultStart); // ✅ Reset to today
    setEndTime(defaultEnd);
    setTripData([]);
    setSummary(null);
    setErrorMessage("");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2} alignItems="center">
                    {/* <Grid item xs={12} md={3}>
                      <Autocomplete
                        options={things || []}
                        getOptionLabel={(option) => option.thingId || ""}
                        value={things.find((t) => t.thingId === boatId) || null}
                        onChange={(e, newValue) => setBoatId(newValue ? newValue.thingId : "")}
                        renderInput={(params) => <TextField {...params} label="Select Boat" size="small" 
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#FB8C00", // ✅ border on click
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#FB8C00", // ✅ label color
                              },
                            }}
                        />}
                      />
                    </Grid> */}

                    {/* ✅ Custom DateRangePicker */}
                    <Grid item xs={12} md={6}>
                      <div style={{ width: '46%' }}>
                        <DateRangePicker
                          startTime={startTime}
                          endTime={endTime}
                          onChange={({ startTime: s, endTime: e }) => {
                            setStartTime(s);
                            setEndTime(e);
                          }}
                          disabled={loading}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <MDButton variant="gradient" color="info" onClick={fetchTripReport} disabled={loading} fullWidth size="small">
                            {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                          </MDButton>
                        </Grid>
                        <Grid item xs={6}>
                          <MDButton variant="outlined" color="error" onClick={handleClear} fullWidth size="small">CLEAR</MDButton>
                        </Grid>
                      </Grid>
                    </Grid>

                  </Grid>
                </LocalizationProvider>

                {/* ✅ SUMMARY DASHBOARD SECTION */}
                {summary && (
                  <MDBox mt={4} p={2} sx={{ backgroundColor: "#f0f2f5", borderRadius: "8px" }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <MDTypography variant="caption" fontWeight="bold" color="text">TOTAL TRIPS</MDTypography>
                        <MDTypography variant="h6">{summary.totalTrips}</MDTypography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <MDTypography variant="caption" fontWeight="bold" color="text">FUEL CONSUMED</MDTypography>
                        <MDTypography variant="h6">{summary.totalFuelConsumed} L</MDTypography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <MDTypography variant="caption" fontWeight="bold" color="text">TOTAL DISTANCE</MDTypography>
                        {/* ✅ Displaying converted distance with NM unit */}
                        <MDTypography variant="h6">{summary.totalDistance} NM</MDTypography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <MDTypography variant="caption" fontWeight="bold" color="text">ENGINE HOURS</MDTypography>
                        <MDTypography variant="h6">{summary.totalEngineHours} HR</MDTypography>
                      </Grid>
                    </Grid>
                  </MDBox>
                )}

                {errorMessage && (
                  <MDBox mt={2}>
                    <Alert severity="error">{errorMessage}</Alert>
                  </MDBox>
                )}

                {/* ✅ TABLE SECTION WITH ALL BINDED DATA */}
                <MDBox mt={4} sx={{ overflowX: "auto" }}>
                  {loading ? (
                    <MDBox display="flex" justifyContent="center"><DhundhooLoader /></MDBox>
                  ) : (
                    <HaltReportTable
                      table={{ columns, rows: tripData }}
                      isSorted={true}
                      entriesPerPage={{ defaultValue: 10, entries: [5, 10, 20, 50] }}
                      showTotalEntries={true}
                      noEndBorder={false}
                      canSearch={true}
                    />
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDSnackbar
        color="success"
        icon="check"
        title="Trip Report"
        content="Report loaded successfully"
        open={successSB}
        onClose={closeSuccessSB}
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title="Trip Report"
        content={errorMessage || "Error loading report"}
        open={errorSB}
        onClose={closeErrorSB}
      />
    </DashboardLayout>
  );
}

export default BoatOrgReport;